/**
 * Types for the ActionButton system.
 *
 * Defines TypeScript interfaces and type aliases for action definitions,
 * panels, and responses. These should match the backend Pydantic models
 * defined in backend/app/schemas/actions.py
 */

/**
 * Types of actions available to players.
 */
export type ActionType = 
  | "vote"      // Voting for a player
  | "ability"   // Using a game ability
  | "confirm"   // Simple confirmation
  | "skip"      // Skip/pass turn
  | "chat"      // Send a message
  | "custom";   // AI-defined custom action

/**
 * Visual variants for action buttons.
 */
export type ActionVariant = "primary" | "secondary" | "danger" | "ghost";

/**
 * Size variants for action buttons.
 */
export type ActionSize = "sm" | "md" | "lg" | "xl";

/**
 * Padding override for action buttons.
 *
 * Same scale as ActionSize: "md" gives the padding of size "md". Overrides only
 * the spacing — typography and the touch-target height still follow the size.
 * "none" removes the padding entirely, which is what dense grids need.
 */
export type ActionPadding = "none" | ActionSize;

/**
 * Types of targeting for actions.
 */
export type TargetType = "none" | "player" | "item";

/**
 * Layout options for action panels.
 */
export type ActionLayout = "vertical" | "horizontal" | "grid";

/**
 * How a panel's action buttons are rendered on the phone.
 *
 * "full": the panel renders all of its action buttons itself.
 * "html_bridge": the game mode renders its own markup in the phone info area
 * and marks clickable elements with `data-action-id`. Actions detected in that
 * HTML are hidden from the panel; the header and any action absent from the
 * HTML stay visible.
 */
export type PanelDisplayMode = "full" | "html_bridge";

/**
 * Option for targeting in an action.
 *
 * Used when action requires player/item selection.
 */
export interface TargetOption {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

/**
 * Option for multiselect panel.
 *
 * Used when player needs to select multiple items from a list.
 */
export interface MultiSelectOption {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Panel for selecting multiple options.
 *
 * Renders as toggleable buttons (not checkboxes).
 * Player can select multiple options within min/max constraints.
 */
export interface MultiSelectPanelDefinition {
  id: string;
  panel_type: "multiselect";
  title?: string;
  description?: string;
  options: MultiSelectOption[];
  min_selections: number;
  max_selections?: number;
  submit_label: string;
  layout?: ActionLayout;
  timeout_seconds?: number;
}

/**
 * Definition of a single action button.
 *
 * Sent from backend to frontend for rendering.
 * Agent can dynamically define these based on game state.
 */
export interface ActionDefinition {
  // Identity
  id: string;
  type: ActionType;
  label: string;

  // Visual
  icon?: string;
  description?: string;
  variant?: ActionVariant;
  size?: ActionSize;
  /** Padding override; undefined keeps the padding implied by `size`. */
  padding?: ActionPadding;

  // Behavior
  requires_confirmation?: boolean;
  confirmation_text?: string;
  cooldown_seconds?: number;
  disabled?: boolean;
  disabled_reason?: string;

  // Data
  payload?: Record<string, unknown>;

  // Targeting
  target_type?: TargetType;
  target_options?: TargetOption[];
}

/**
 * Panel containing multiple actions for player.
 *
 * Sent via WebSocket when backend needs player input.
 */
export interface ActionPanelDefinition {
  id: string;
  panel_type: "action";
  title?: string;
  description?: string;
  actions: ActionDefinition[];
  layout?: ActionLayout;
  timeout_seconds?: number;
  display_mode?: PanelDisplayMode;
  selectedPlayerId?: string; // Added for voting/targeting
}

/**
 * A single occupied cell of a GridPanelDefinition.
 *
 * Positions without an entry in `cells` are rendered as empty space.
 */
export interface GridCellDefinition {
  /** 0-based row index. */
  row: number;
  /** 0-based column index. */
  col: number;
  action: ActionDefinition;
}

/**
 * Panel rendering actions on a fixed grid, positioned by explicit row/col.
 *
 * Used when the phone layout must mirror a board shown on the TV.
 * The `layout` field inherited from other panels is unused here.
 * A click on a cell produces the regular action response.
 */
export interface GridPanelDefinition {
  id: string;
  panel_type: "grid";
  title?: string;
  description?: string;
  rows: number;
  columns: number;
  cells: GridCellDefinition[];
  timeout_seconds?: number;
  display_mode?: PanelDisplayMode;
}

/**
 * Option for form fields (radio, checkbox).
 */
export interface FormOption {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  description?: string;
}

/**
 * Base interface for form field definitions.
 */
export interface FormFieldBase {
  id: string;
  label: string;
  description?: string;
  field_type: string;
}

/**
 * Text input field definition.
 */
export interface TextInputFieldDef extends FormFieldBase {
  field_type: "text_input";
  placeholder?: string;
  max_length?: number;
  required?: boolean;
}

/**
 * Text input field with an AI suggestion button.
 *
 * Behaves like TextInputFieldDef, plus a button below the input that requests
 * AI-generated suggestions from the backend game mode.
 */
export interface AITextInputFieldDef extends FormFieldBase {
  field_type: "ai_text_input";
  placeholder?: string;
  max_length?: number;
  required?: boolean;
  suggest_button_label: string;
  suggest_loading_label: string;
  /** Server-side usage limit already consumed — render the button disabled. */
  suggest_disabled?: boolean;
}

/**
 * Result of an AI suggestion request for a form field.
 */
export interface AISuggestResult {
  suggestions: string[];
  error?: string | null;
}

/**
 * Textarea field definition.
 */
export interface TextAreaFieldDef extends FormFieldBase {
  field_type: "textarea";
  placeholder?: string;
  max_length?: number;
  rows?: number;
  required?: boolean;
}

/**
 * Radio group field definition.
 */
export interface RadioFieldDef extends FormFieldBase {
  field_type: "radio";
  options: FormOption[];
  required?: boolean;
}

/**
 * Slider (segmented control) field definition.
 *
 * Rendered as a horizontal row of pill buttons.
 * Semantically identical to RadioField (single selection).
 */
export interface SliderFieldDef extends FormFieldBase {
  field_type: "slider";
  options: FormOption[];
  required?: boolean;
}

/**
 * Checkbox group field definition.
 */
export interface CheckboxFieldDef extends FormFieldBase {
  field_type: "checkbox";
  options: FormOption[];
  min_selections?: number;
  /** Backend sends `null` (not `undefined`) when the field has no upper limit. */
  max_selections?: number | null;
}

/**
 * Row or column header of an action grid field.
 *
 * Headers live outside the action area: row headers form an extra column on the
 * left, column headers an extra row on top.
 */
export interface GridHeader {
  label: string;
  icon?: string | null;
  /** Show the icon alone; the label stays in the DOM for screen readers. */
  label_hidden?: boolean;
}

/**
 * Grid-of-actions field definition.
 *
 * Clicking a cell answers the whole panel immediately (ActionPanelResponse) and
 * closes the barrier — values held by the other fields of the same form are lost,
 * because the form submit never happens.
 *
 * `rows`/`columns` describe the action area alone; headers do not shift cell
 * coordinates.
 */
export interface ActionGridFieldDef extends FormFieldBase {
  field_type: "action_grid";
  rows: number;
  columns: number;
  cells: GridCellDefinition[];
  row_headers?: GridHeader[];
  col_headers?: GridHeader[];
}

/**
 * Discriminated union of all form field types.
 */
export type FormFieldDefinition =
  | TextInputFieldDef
  | AITextInputFieldDef
  | TextAreaFieldDef
  | RadioFieldDef
  | SliderFieldDef
  | CheckboxFieldDef
  | ActionGridFieldDef;

/**
 * Panel for multi-field form input.
 */
export interface FormPanelDefinition {
  id: string;
  panel_type: "form";
  title?: string;
  description?: string;
  fields: FormFieldDefinition[];
  /** Absent only when every field is an action_grid — such a form answers on cell click. */
  submit_label?: string | null;
  show_progress?: boolean;
  layout?: ActionLayout;
  timeout_seconds?: number;
}

/**
 * Union type for question panels (discriminated by panel_type).
 */
export type QuestionPanelDefinition =
  | ActionPanelDefinition
  | MultiSelectPanelDefinition
  | FormPanelDefinition
  | GridPanelDefinition;

/**
 * Type guard for grid panels.
 */
export function isGridPanel(
  panel: QuestionPanelDefinition
): panel is GridPanelDefinition {
  return panel.panel_type === "grid";
}

/**
 * Submit payload for action panel response.
 */
export interface SubmitActionPayload {
  response_type: "action";
  action_id: string;
  player_id: string;
  request_id: string;
  target_id?: string;
  timestamp: number;
}

/**
 * Submit payload for multiselect panel response.
 */
export interface SubmitMultiSelectPayload {
  response_type: "multiselect";
  player_id: string;
  request_id: string;
  selected_ids: string[];
  timestamp: number;
}

/**
 * Submit payload for form panel response.
 */
export interface SubmitFormPayload {
  response_type: "form";
  player_id: string;
  request_id: string;
  field_values: Record<string, string | string[]>;
  timestamp: number;
}

/**
 * Discriminated union of all submit response payloads.
 */
export type SubmitResponsePayload =
  | SubmitActionPayload
  | SubmitMultiSelectPayload
  | SubmitFormPayload;
